import { useEffect, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Box, Button, Grid } from '@mui/material';
import logger from '@/lib/clientLogger';
import Header from '@/components/Header';
import useFlowNavigation from '@/components/features/flowNavigation/useFlowNavigation';
import {
  useAppDispatch,
  useFlowState,
  useSeekerCCState,
  useSeekerState,
} from '@/components/AppState';
import ZipInput from '@/components/ZipInput';
import { logChildCareEvent } from '@/utilities/childCareAnalyticsHelper';
import { ServiceIdsForMember } from '@/types/seekerCC';
import { Location } from '@/types/common';
import { ServiceType, YesOrNoAnswer } from '@/__generated__/globalTypes';
import useEnterKey from '@/components/hooks/useEnterKey';
import { useCommonSharedData } from '@/components/hooks/useCommonSharedData';
import { useFeatureFlags } from '@/components/FeatureFlagsContext';
import { CLIENT_FEATURE_FLAGS, FLOWS } from '@/constants';
import { AnalyticsHelper } from '@/utilities/analyticsHelper';
import Map from '@/components/Map';
import useProviderCount from '@/components/hooks/useProviderCount';
import useResponsive from '@/components/hooks/useResponsive';
import useIsSeekerEnrollment2 from '@/components/hooks/useIsSeekerEnrollment2';
import StickyCTAButton from '@/components/shared/StickyCTAButton/StickyCTAButton';

function CareLocation() {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const sendLeadEnabledQueryParam = router.query;
  const [shouldGoNext, setShouldGoNext] = useState(false);
  const [error, setError] = useState(false);
  const [validateOnClick, setValidateOnClick] = useState(false);
  const [latLng, setLatLng] = useState<Partial<Location>>({
    lat: undefined,
    lng: undefined,
  });
  const { enrollmentSource, czenServiceIdForMember } = useSeekerCCState();
  const {
    landingPageZipcode,
    zipcode,
    city,
    state,
    visitorStartedSACFlow,
    maxDistanceFromSeekerDayCare,
    isUserAssignedToSkipSteps,
    queryParamMetadata,
  } = useSeekerState();
  const { flowName } = useFlowState();
  const { goNext } = useFlowNavigation();
  const { publishCommonSharedData } = useCommonSharedData();
  const { numOfProviders: numCaregivers, displayProviderMessage: displayMap } = useProviderCount(
    ServiceType.CHILD_CARE,
    undefined,
    undefined,
    !error
  );
  const { isSeekerEnrollment2Eligible } = useIsSeekerEnrollment2(ServiceType.CHILD_CARE);
  const { isDesktop } = useResponsive();

  const featureFlags = useFeatureFlags();
  const shouldShowSeekerMultiVertical =
    featureFlags?.flags[CLIENT_FEATURE_FLAGS.GROWTH_CAPTURE_MULTI_VERTICAL]?.variationIndex === 2;
  const mappingOnZipcode = featureFlags?.flags[CLIENT_FEATURE_FLAGS.GROWTH_CC_MAPPING_ON_ZIPCODE];
  const isLocationWithMapPage = mappingOnZipcode?.value === 2;

  const logInitialFlowStart = () => {
    if (!visitorStartedSACFlow) {
      dispatch({ type: 'setVisitorStartedSACFlow', visitorStartedSACFlow: true });
      logger.info({ event: 'seekerAccountCreationStarts' });
    }

    if (shouldShowSeekerMultiVertical && !isSeekerEnrollment2Eligible) {
      dispatch({
        type: 'setFlowName',
        flowName: FLOWS.SEEKER_CHILD_CARE_WITH_ADD_ON_VERTICALS.name,
      });
    } else {
      dispatch({ type: 'setFlowName', flowName });
    }
  };

  const onZipInputChange = (newLocation: Location) => {
    dispatch({ type: 'setZipcode', zipcode: newLocation.zipcode });
    dispatch({ type: 'setCityAndState', city: newLocation.city, state: newLocation.state });
    setLatLng(newLocation);
  };

  const handleError = (e: boolean) => {
    setError(e);
  };

  useEnterKey(true, () => setShouldGoNext(true), true);

  useEffect(() => {
    logInitialFlowStart();
  }, []);

  useEffect(() => {
    if (sendLeadEnabledQueryParam?.sendLeadEnabled) {
      const isSendLeadEnabledFlag = sendLeadEnabledQueryParam.sendLeadEnabled !== 'false';
      dispatch({
        type: 'cc_setIsSendLeadEnabled',
        isSendLeadEnabled: isSendLeadEnabledFlag,
      });
    }
  }, []);

  useEffect(() => {
    if (!shouldGoNext) {
      return;
    }

    setValidateOnClick(true);

    if (error) {
      return;
    }

    logChildCareEvent(
      'Member Enrolled - Seeker',
      'Care Location',
      enrollmentSource,
      {
        zip: zipcode,
        city,
        state,
        referer: document.referrer,
        screen_name: 'Care Location',
        ...(czenServiceIdForMember === ServiceIdsForMember.dayCare && {
          extraData: {
            user_distance: maxDistanceFromSeekerDayCare.distance,
            user_distance_unit: maxDistanceFromSeekerDayCare.unit,
          },
        }),
        ...(isLocationWithMapPage && { number_of_providers: numCaregivers }),
        ...(landingPageZipcode && {
          pre_populated_zip: landingPageZipcode,
          zip_modified: landingPageZipcode !== zipcode,
        }),
      },
      'Next'
    );
    // if intent (care date) supplied, then skip the next page
    const skipCareDateStep =
      isUserAssignedToSkipSteps === YesOrNoAnswer.YES && queryParamMetadata?.intentSupplied;

    if (isSeekerEnrollment2Eligible) {
      goNext();
      return;
    }

    goNext(skipCareDateStep);
  }, [shouldGoNext, city, error]);

  useEffect(() => {
    publishCommonSharedData();
  }, [zipcode, city, state]);

  useEffect(() => {
    AnalyticsHelper.logTestExposure(
      CLIENT_FEATURE_FLAGS.GROWTH_CC_MAPPING_ON_ZIPCODE,
      mappingOnZipcode
    );
  }, []);

  return (
    <>
      <Head>
        <title>Where do you need care?</title>
      </Head>
      <Grid container px={1}>
        <Grid item xs={12} mb={3}>
          <Header variant={isSeekerEnrollment2Eligible && isDesktop ? 'h1' : 'h2'}>
            Where do you need care?
          </Header>
        </Grid>

        <Grid item xs={12}>
          <ZipInput
            location={{ zipcode, city, state }}
            onError={handleError}
            onChange={onZipInputChange}
            validateOnLostFocusOrClick
            validateOnClick={validateOnClick}
            setValidateOnClick={setValidateOnClick}
            setShouldGoNext={setShouldGoNext}
          />
        </Grid>

        {isLocationWithMapPage && !error && displayMap && (
          <Grid item xs={12}>
            <Box my={2}>
              <Map
                count={numCaregivers}
                lat={latLng.lat}
                lng={latLng.lng}
                serviceType={ServiceType.CHILD_CARE}
                isSeeker
              />
            </Box>
          </Grid>
        )}

        {!isDesktop && isSeekerEnrollment2Eligible && router.pathname !== '/seeker/dc/location' ? (
          <StickyCTAButton onClick={() => setShouldGoNext(true)} ctaText="Next" />
        ) : (
          <Grid item xs={12} my={4}>
            <Button
              onClick={() => setShouldGoNext(true)}
              variant="contained"
              color="primary"
              fullWidth
              size="large">
              Next
            </Button>
          </Grid>
        )}
      </Grid>
    </>
  );
}

export default CareLocation;
